import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// Reemplaza los alert()/confirm()/prompt() nativos del navegador en todo
// el sistema (ver hook useModal en ../../hooks/useModal.js).

const MODAL_TYPES = {
  success: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200', btnColor: 'bg-green-600 hover:bg-green-700' },
  error:   { icon: XCircle,      color: 'text-red-500',   bg: 'bg-red-50',   border: 'border-red-200',   btnColor: 'bg-red-600 hover:bg-red-700'   },
  warning: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', btnColor: 'bg-amber-600 hover:bg-amber-700' },
  info:    { icon: Info,          color: 'text-brand-500',  bg: 'bg-brand-50',  border: 'border-brand-200',  btnColor: 'bg-brand-600 hover:bg-brand-700'  },
  confirm: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', btnColor: 'bg-red-600 hover:bg-red-700'   },
  prompt:  { icon: Info,          color: 'text-brand-500',  bg: 'bg-brand-50',  border: 'border-brand-200',  btnColor: 'bg-brand-600 hover:bg-brand-700'  },
};

const NotificationModal = ({ type = 'info', title, message, confirmLabel = 'Aceptar', cancelLabel = 'Cancelar', onConfirm, onCancel, inputLabel, inputPlaceholder, onClose }) => {
  const cfg = MODAL_TYPES[type] || MODAL_TYPES.info;
  const Icon = cfg.icon;
  const [inputValue, setInputValue] = useState('');
  const isConfirm = type === 'confirm';
  const isPrompt  = type === 'prompt';
  const showCancel = isConfirm || isPrompt;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-in fade-in duration-200">
      <div className={`bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border-2 ${cfg.border}`}>
        {/* Header */}
        <div className={`${cfg.bg} px-6 pt-6 pb-4 flex items-start gap-4`}>
          <div className={`flex-shrink-0 p-2 rounded-full bg-white shadow-sm`}>
            <Icon size={28} className={cfg.color} />
          </div>
          <div className="flex-1 min-w-0">
            {title && <h3 className="font-bold text-slate-800 text-lg leading-tight">{title}</h3>}
            {message && <p className="text-slate-600 mt-1 text-sm leading-relaxed whitespace-pre-line">{message}</p>}
          </div>
          <button onClick={onClose || onCancel} className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 rounded-lg transition">
            <X size={18} />
          </button>
        </div>

        {/* Input para prompt */}
        {isPrompt && (
          <div className="px-6 py-3 bg-white">
            {inputLabel && <label className="block text-sm font-medium text-slate-700 mb-1.5">{inputLabel}</label>}
            <input
              autoFocus
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && inputValue.trim() && onConfirm(inputValue)}
              placeholder={inputPlaceholder || ''}
              className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
            />
          </div>
        )}

        {/* Footer */}
        <div className="px-6 pb-6 pt-4 flex justify-end gap-3">
          {showCancel && (
            <button
              onClick={onCancel}
              className="px-5 py-2.5 border-2 border-slate-300 rounded-xl font-medium text-sm hover:bg-slate-50 transition"
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={() => {
              if (isPrompt) { if (inputValue.trim()) onConfirm(inputValue); }
              else if (onConfirm) onConfirm();
              else if (onClose) onClose();
            }}
            disabled={isPrompt && !inputValue.trim()}
            className={`px-5 py-2.5 ${cfg.btnColor} text-white rounded-xl font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
